import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Icon, SimpleModal, toast } from '@livable/saas-react-components';
import { Button, Chip, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useStore } from 'src/stores';
import { ACCEPTED_BILL_FILE_TYPES } from 'src/stores/billAutomation/bulkUpload';
import { MAX_BILL_UPLOAD_MB } from 'src/stores/billAutomation/uploadBillFile';
import { billContentTypeOf } from 'src/utils/billFileType';
import { getRetryAllocationNote } from './archivedAllocationWording';
import useStyles from './retryBillModalStyles';

/**
 * The same words whichever reader comes next. Which OCR stages and models read a bill is ours, not the
 * property manager's, so the service's `currentStage` / `nextStage` pick the read and are never shown.
 */
const EXISTING_FILE_HINT = 'It will be read again, more thoroughly than last time.';

/**
 * Retry a bill (LB-5787). The bill is archived and a new one is built from a fresh read — of its own
 * file, read again one stage stronger than last time, or of a better file the person uploads.
 *
 * What may be offered is the service's to say (`retry-options`), so the dialog asks when it opens:
 * whether the bill can be retried at all, whether its own file can be read again, and why not. The
 * row only gates the obvious cases — allocated, pushed, still being read — before that answer lands.
 */
export const RetryBillModal = observer(_ref => {
  let row = _ref.row,
    isOpen = _ref.isOpen,
    handleClose = _ref.handleClose,
    onRetried = _ref.onRetried;
  const store = useStore();
  const classes = useStyles();
  const _useState = useState({
      options: null,
      isLoading: false,
      mode: 'existing',
      file: null,
      fileError: null,
      isSaving: false
    }),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  const set = patch => setState(previous => _objectSpread(_objectSpread({}, previous), patch));
  const billId = row === null || row === void 0 ? void 0 : row.id;
  const getBillRetryOptionsAsync = store.companyBills.getBillRetryOptionsAsync;
  useEffect(() => {
    if (!isOpen || !billId) return undefined;
    let cancelled = false;
    set({
      options: null,
      isLoading: true,
      mode: 'existing',
      file: null,
      fileError: null,
      isSaving: false
    });
    getBillRetryOptionsAsync.run({
      billId
    }).then(resp => {
      if (cancelled) return;
      const options = resp !== null && resp !== void 0 && resp.isSuccessful ? resp.response : null;
      // Default to whichever choice is actually available.
      set({
        options,
        isLoading: false,
        mode: (options === null || options === void 0 ? void 0 : options.canUseExistingFile) === false ? 'new' : 'existing'
      });
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, billId, getBillRetryOptionsAsync]);
  const options = state.options;
  const canRetry = !!(options !== null && options !== void 0 && options.canRetry);
  const canUseExistingFile = canRetry && !!(options !== null && options !== void 0 && options.canUseExistingFile);
  const handleFileChange = event => {
    var _event$target$files;
    const file = ((_event$target$files = event.target.files) === null || _event$target$files === void 0 ? void 0 : _event$target$files[0]) || null;
    if (!file) {
      set({
        file: null,
        fileError: null
      });
      return;
    }
    if (!billContentTypeOf(file)) {
      set({
        file: null,
        fileError: 'Choose a PDF or an image (PNG, JPG, GIF or WEBP).'
      });
      return;
    }
    if (file.size > MAX_BILL_UPLOAD_MB * 1024 * 1024) {
      set({
        file: null,
        fileError: "Choose a file under ".concat(MAX_BILL_UPLOAD_MB, " MB.")
      });
      return;
    }
    set({
      file,
      fileError: null
    });
  };
  const handleRetry = async () => {
    if (!row) return;
    set({
      isSaving: true
    });

    // The async layer catches whatever goes wrong on the way — the file failing to upload included —
    // toasts it itself and resolves to nothing, so there is no throw to catch here: nothing back means
    // it has already been said. A 409 arrives as an answer, carrying the service's reason.
    const resp = await store.companyBills.retryBillAsync.run(state.mode === 'new' ? {
      billId,
      mode: 'new',
      file: state.file,
      contentType: billContentTypeOf(state.file)
    } : {
      billId,
      mode: 'existing'
    });
    set({
      isSaving: false
    });
    if (!resp || resp.isSuccessful === false) {
      if (resp !== null && resp !== void 0 && resp.message) toast('error', {
        title: 'Bill not retried',
        body: [resp.message]
      });
      return;
    }
    toast('success', {
      body: [state.mode === 'new' ? "".concat(row.fileName, " was archived. The new file is being read.") : "".concat(row.fileName, " was archived. Its file is being read again.")]
    });
    onRetried === null || onRetried === void 0 || onRetried();
  };
  const canSubmit = canRetry && !state.isSaving && (state.mode === 'existing' ? canUseExistingFile : !!state.file && !state.fileError);
  const existingFileHint = canUseExistingFile ? EXISTING_FILE_HINT : options === null || options === void 0 ? void 0 : options.existingFileReason;
  return /*#__PURE__*/React.createElement(SimpleModal, {
    isOpen: isOpen,
    handleClose: handleClose
    // Its own buttons are the dialog's; the modal's footer would add a second Cancel beside them.
    ,
    noFooter: true,
    title: "Retry this bill?",
    subTitle: (row === null || row === void 0 ? void 0 : row.fileName) || undefined
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.content
  }, /*#__PURE__*/React.createElement(Chip, {
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "info"
    }),
    label: 'Retrying archives this bill and creates a new one from a fresh read. Any edits you made stay with the archived bill.' + (row !== null && row !== void 0 && row.allocationCount ? " ".concat(getRetryAllocationNote(row.allocationCount)) : ''),
    variant: "info"
  }), state.isLoading && /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    color: "light"
  }, "Checking what can be retried\u2026"), !state.isLoading && options && !canRetry && /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    className: classes.refusal
  }, options.reason), !state.isLoading && !options && /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    className: classes.refusal
  }, "We couldn't check this bill just now. Close this and try again."), canRetry && /*#__PURE__*/React.createElement(RadioGroup, {
    value: state.mode,
    onChange: event => set({
      mode: event.target.value
    })
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Radio, null),
    disabled: !canUseExistingFile || state.isSaving,
    label: "Use the existing file",
    value: "existing"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    className: classes.hint
  }, existingFileHint), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Radio, null),
    disabled: state.isSaving,
    label: "Upload a new file",
    value: "new"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    className: classes.hint
  }, "A clearer copy of the same bill, read from the start."), state.mode === 'new' && /*#__PURE__*/React.createElement("div", {
    className: classes.fileField
  }, /*#__PURE__*/React.createElement("input", {
    accept: ACCEPTED_BILL_FILE_TYPES,
    "aria-label": "Replacement bill file",
    disabled: state.isSaving,
    onChange: handleFileChange,
    type: "file"
  }), state.fileError && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.refusal
  }, state.fileError))), /*#__PURE__*/React.createElement("div", {
    className: classes.actions
  }, canRetry && /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    disabled: !canSubmit,
    onClick: handleRetry
  }, state.isSaving ? 'Retrying…' : 'Retry bill'), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    disabled: state.isSaving,
    onClick: handleClose
  }, canRetry ? 'Cancel' : 'Close'))));
});